import React, { Component } from 'react';
import autoBind from 'react-autobind';
import classnames from 'classnames';
import { injectIntl } from 'react-intl';
import { withRouter } from 'react-router';

import routeGenerators from 'ui/common/routes/generators';
import ProgressBar from 'ui/common/rebranding/components/ProgressBar';
import { truncate } from 'utils/string.js';
import messages from './messages';
import styles from './index.module.scss';
import indorsedIcon from 'resources/common/indorsed.svg';
import flaggedIcon from 'resources/common/flagged.svg';
import logoMeta from 'resources/brand/logo-icon.png';
import Button from 'ui/common/button';
import InternalLink from 'ui/common/internal-link';
import Countdown from 'ui/common/countdown/loadable';
import { parseApiDate } from 'store/common/schemas';
import { ensureScheme } from 'utils/url';

import themeConfig from 'ui/theme/config';
import ProgressChart from 'ui/common/charts/progress-chart'; // progress bar and chart are two diff components

import check from './check.svg';

class ClaimListItem extends Component {
  constructor(props) {
    super(props);
    autoBind(this);
  }

  renderLabels(type) {
    const { intl: { formatMessage } } = this.props;
    if (type === 'indorsed') {
      return (
        <Button svgIcon={indorsedIcon} iconPosition="prefix" className="cat-health gradient px-4">
          <span className="ml-2">{formatMessage(messages.indorsed)}</span>
        </Button>
      );
    } else if (type === 'flagged') {
      return (
        <Button
          svgIcon={flaggedIcon}
          iconPosition="prefix"
          className="cat-technology gradient px-4"
        >
          <span className="ml-2">{formatMessage(messages.flagged)}</span>
        </Button>
      );
    } else if (type === 'disapproved') {
      return (
        <Button disabled className={styles.disabledCursor} isReady={false}>
          {formatMessage(messages.claimDisapproved)}
        </Button>
      );
    } else if (type === 'noconsensus') {
      return (
        <Button disabled className={styles.disabledCursor} isReady={false}>
          {formatMessage(messages.noConsensus)}
        </Button>
      );
    } else {
      return (
        <Button disabled className={styles.disabledCursor} isReady={false}>
          {formatMessage(messages.pendingValidation)}
        </Button>
      );
    }
  }

  renderClaimStatuses(claim) {
    const { intl: { formatMessage } } = this.props;

    if (claim) {
      const {
        claimStatus,
        claim_expiry,
        claimSummary: { votesThreshold, completedVoteCount },
      } = claim;

      const inProgress = claimStatus === 'claim_submitted' || claimStatus === 'in_progress';
      const showProgressBar = claimStatus === 'in_progress' && claim_expiry;
      const isFlagged = claimStatus === 'rejected';
      const isIndorsed = claimStatus === 'indorsed';
      const isDisapproved = claimStatus === 'claim_disapproved';
      const isNoConsensus = claimStatus === 'no_consensus';

      return (
        <div>
          {inProgress && this.renderLabels('pending')}
          {inProgress &&
            showProgressBar && (
              <div className="py-3">
                <ProgressBar
                  min={0}
                  max={votesThreshold}
                  value={completedVoteCount}
                  label="Votes"
                />
              </div>
            )}
          {isIndorsed && <div>{this.renderLabels('indorsed')}</div>}
          {isFlagged && (
            <div className="d-flex flex-column align-items-end">
              <div>{this.renderLabels('flagged')}</div>
            </div>
          )}
          {isDisapproved && (
            <div className="d-flex flex-column align-items-end">
              <div>{this.renderLabels('disapproved')}</div>
            </div>
          )}
          {isNoConsensus && (
            <div className="d-flex flex-column align-items-end">
              <div>{this.renderLabels('noconsensus')}</div>
            </div>
          )}
          {showProgressBar && (
            <div className="d-flex justify-content-end">
              <div>{formatMessage(messages.votingExpiry)}</div>
              <div className="ml-2">
                <b>
                  <Countdown deadline={parseApiDate(claim_expiry)} />
                </b>
              </div>
            </div>
          )}
        </div>
      );
    }
  }

  renderProgressChart(claim) {
    let dataSets = [];

    const { claimSummary: { indorsedVotesCount, flaggedVotesCount } } = claim;

    if (claim) {
      dataSets = [
        {
          label: 'Indorsed',
          value: indorsedVotesCount,
          color: [themeConfig.palette.category.healthA, themeConfig.palette.category.healthB],
        },
        {
          label: 'Flagged',
          value: flaggedVotesCount,
          color: [
            themeConfig.palette.category.technologyA,
            themeConfig.palette.category.technologyB,
          ],
        },
      ];
    }

    return (
      <div className="w-100">
        {dataSets &&
          dataSets.length > 0 && (
            <div className={classnames('d-flex pb-3 justify-content-center', styles.progressChart)}>
              <div>
                <ProgressChart labelLegend="Votes" height={70} width={60} dataSets={dataSets} />
              </div>
              <div>
                <ul>
                  {dataSets.map((data, index) => {
                    return (
                      <li className="mt-2 ml-2" key={index}>
                        <p>
                          <span>{data.value} votes</span>
                        </p>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>
          )}
      </div>
    );
  }

  render() {
    const { claim, intl: { formatMessage } } = this.props;

    if (claim) {
      const { title, skills, desc, _id, claimStatus } = claim;

      const inProgress = claimStatus === 'submitted';

      const { iconUrl } = (skills.length > 0 && skills[0]) || {};

      return (
        <div key={_id} className="row justify-content-md-center pb-3">
          <div className={classnames('col-12')}>
            <div
              className={classnames(
                'px-3 border',
                styles.whiteSmokeBorder,
                claimStatus === 'indorsed' ? styles.card_border : ''
              )}
            >
              <div className={classnames('row align-items-center py-4 px-md-4')}>
                {claimStatus === 'indorsed' && (
                  <img
                    className={classnames('position-absolute')}
                    src={check}
                    height="20px"
                    width="20px"
                    alt="checkbox"
                    style={{ top: '1rem', left: '2rem' }}
                  />
                )}

                {/* <MediaQuery minWidth={themeConfig.layout.gridBreakpoints.md}> */}
                {iconUrl && (
                  <div className="col col-sm-12 col-md-3">
                    <img
                      src={ensureScheme(iconUrl) || logoMeta}
                      className="mx-auto"
                      alt={title}
                      width={150}
                      height={150}
                    />
                  </div>
                )}
                {/* </MediaQuery> */}

                <div className="col-12 col-sm-12 col-md-6 py-2">
                  {title && (
                    <h2 className="text-center text-md-left">
                      <InternalLink
                        to={routeGenerators.claims.details({ id: claim._id })}
                        className={styles.noDecoration}
                      >
                        {title}
                      </InternalLink>
                    </h2>
                  )}
                  <p className="py-2">{formatMessage(messages.claimDescription)}</p>
                  {desc && <p>{truncate(desc, 200)}</p>}
                </div>
                <div className="col-12 col-sm-12 py-3 py-md-0 col-md-3">
                  <div className="d-flex justify-content-center justify-content-md-start">
                    {claimStatus === 'indorsed' && this.renderProgressChart(claim)}
                  </div>
                  <div className="d-flex justify-content-center justify-content-md-end">
                    {inProgress && this.renderLabels('pending')}
                    {this.renderClaimStatuses(claim)}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }
    return null;
  }
}

export default withRouter(injectIntl(ClaimListItem));
